import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Buildings, CloudSun, Wind, Warning, Waveform, VideoCamera, ShieldCheck,
  Plus, Trash, CloudLightning, ArrowClockwise,
} from "@phosphor-icons/react";

const WMO = { 0: "Clear", 1: "Clear", 2: "Cloudy", 3: "Overcast", 45: "Fog", 61: "Rain", 63: "Rain", 65: "Heavy rain", 71: "Snow", 75: "Heavy snow", 80: "Showers", 82: "Violent showers", 95: "Thunderstorm", 96: "Storm+hail", 99: "Severe storm" };

export default function StoreBoard() {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", lat: "", lon: "" });

  const load = () => api.get("/intel/store-status").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/intel/weather-check");
      const alerted = data.results.filter((r) => r.alerted);
      if (alerted.length) toast.error(`Severe weather at ${alerted.length} store(s)`, { description: alerted.map((a) => a.store).join(", ") });
      else toast.success("Weather checked — all stores clear");
      load();
    } catch (e) { toast.error("Weather check failed"); }
    finally { setChecking(false); }
  };

  const addStore = async (e) => {
    e.preventDefault();
    if (!form.name || form.lat === "" || form.lon === "") { toast.error("Name, lat & lon required"); return; }
    try {
      await api.post("/stores", { name: form.name, city: form.city, lat: Number(form.lat), lon: Number(form.lon) });
      toast.success("Store added");
      setForm({ name: "", city: "", lat: "", lon: "" }); setAdding(false); load();
    } catch { toast.error("Failed to add store"); }
  };

  const removeStore = async (id) => {
    try { await api.delete(`/stores/${id}`); toast.success("Store removed"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!data) return <div className="text-sm text-[#71717A] font-mono-data">Loading board…</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-2 font-mono-data"><Buildings size={16} className="text-[#007AFF]" /> {data.stores.length} stores</span>
          <span className="flex items-center gap-2 font-mono-data"><VideoCamera size={16} className="text-[#32D74B]" /> {data.cameras_online} cameras online</span>
        </div>
        <div className="flex gap-2">
          <button data-testid="add-store-btn" onClick={() => setAdding((a) => !a)} className="flex items-center gap-1.5 bg-[#1A1A1E] border border-white/10 hover:border-[#007AFF] text-white px-3 py-2 text-sm transition-colors"><Plus size={15} /> Add Store</button>
          <button data-testid="weather-check-btn" onClick={runCheck} disabled={checking} className="flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#3395FF] text-white px-3 py-2 text-sm font-display font-bold uppercase tracking-wide transition-colors disabled:opacity-60"><ArrowClockwise size={15} weight="bold" className={checking ? "animate-spin" : ""} /> {checking ? "Checking" : "Run Weather Check"}</button>
        </div>
      </div>

      {adding && (
        <form onSubmit={addStore} className="bg-[#121214] border border-white/10 p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="add-store-form">
          <div className="col-span-2 md:col-span-1"><label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1">Name</label><input data-testid="store-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#1A1A1E] border border-white/10 px-2 py-2 text-sm outline-none focus:border-[#007AFF]" /></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full bg-[#1A1A1E] border border-white/10 px-2 py-2 text-sm outline-none focus:border-[#007AFF]" /></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1">Latitude</label><input data-testid="store-lat" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="w-full bg-[#1A1A1E] border border-white/10 px-2 py-2 text-sm font-mono-data outline-none focus:border-[#007AFF]" /></div>
          <div><label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1">Longitude</label><input data-testid="store-lon" value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })} className="w-full bg-[#1A1A1E] border border-white/10 px-2 py-2 text-sm font-mono-data outline-none focus:border-[#007AFF]" /></div>
          <button type="submit" data-testid="store-save" className="bg-[#007AFF] hover:bg-[#3395FF] text-white py-2 text-sm font-display font-bold uppercase tracking-wide transition-colors">Save</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.stores.map((s) => (
          <div key={s.id} data-testid={`store-card-${s.id}`} className={`bg-[#121214] border p-4 ${s.status === "alert" ? "border-[#FF3B30]/50" : "border-white/10"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${s.status === "alert" ? "bg-[#FF3B30] live-dot" : "bg-[#32D74B]"}`} />
                  <h3 className="font-display font-bold tracking-tight">{s.name}</h3>
                </div>
                <p className="font-mono-data text-[10px] text-[#71717A] mt-1">{s.city} · {s.lat.toFixed(2)}, {s.lon.toFixed(2)}</p>
              </div>
              <button data-testid={`del-store-${s.id}`} onClick={() => removeStore(s.id)} className="text-[#71717A] hover:text-[#FF3B30] transition-colors"><Trash size={15} /></button>
            </div>

            <div className="flex items-center gap-2 mt-4 mb-3">
              {s.severe ? <CloudLightning size={26} weight="duotone" className="text-[#FF3B30]" /> : <CloudSun size={26} weight="duotone" className="text-[#FF9F0A]" />}
              <div>
                <p className="font-mono-data text-2xl leading-none">{s.temp != null ? `${Math.round(s.temp)}°` : "—"}</p>
                <p className="text-[10px] text-[#71717A]">{WMO[s.weather_code] || "—"} · {Math.round(s.wind)} km/h</p>
              </div>
              {s.severe && <span className="ml-auto font-mono-data text-[10px] px-1.5 py-0.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 uppercase">Severe</span>}
            </div>

            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/10">
              <div className="text-center"><Warning size={15} className="mx-auto text-[#FF9F0A]" /><p className="font-mono-data text-sm mt-1">{s.open_alerts}</p><p className="text-[9px] text-[#71717A] uppercase">Alerts</p></div>
              <div className="text-center"><Waveform size={15} className="mx-auto text-[#FF3B30]" /><p className="font-mono-data text-sm mt-1">{s.nearby_quakes}</p><p className="text-[9px] text-[#71717A] uppercase">Quakes ~500km</p></div>
              <div className="text-center"><VideoCamera size={15} className="mx-auto text-[#32D74B]" /><p className="font-mono-data text-sm mt-1">{s.public_cams}</p><p className="text-[9px] text-[#71717A] uppercase">Pub Cams</p></div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4 flex items-start gap-3">
        <ShieldCheck size={18} weight="fill" className="text-[#3395FF] shrink-0 mt-0.5" />
        <p className="text-sm text-[#E4E4E7] leading-relaxed">"Run Weather Check" evaluates every store's live forecast and raises an alert (plus SMS/email/browser notification, if configured in Alert Delivery) when severe weather or high winds hit a location. Add all your stores to watch the whole chain from one board.</p>
      </div>
    </div>
  );
}
